import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  AttachMoney as AttachMoneyIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getPaymentHistory, getPaymentStatistics } from '../services/paymentService';
import {
  DATE_FILTER_OPTIONS,
  SOURCE_TYPE_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_COLORS
} from '../constants/PaymentConstants';
import { getDateRangeFromFilter, formatDateTime, formatCurrency } from '../utils/paymentUtils';

const PaymentPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Filter States
  const [dateFilter, setDateFilter] = useState('last30days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');

  // Data States
  const [payments, setPayments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statisticsLoading, setStatisticsLoading] = useState(false);

  // Initialize date range on mount
  useEffect(() => {
    const dateRange = getDateRangeFromFilter('last30days');
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
    // Load initial data
    loadPaymentData(dateRange.startDate, dateRange.endDate, '', '');
    // eslint-disable-next-line
  }, []);

  const loadPaymentData = async (start = startDate, end = endDate, source = sourceType, method = paymentMethod) => {
    if (!start || !end) {
      toast.warning(t('please_select_date_range') || 'Please select a date range');
      return;
    }

    setLoading(true);
    setStatisticsLoading(true);

    try {
      const params = {
        startDate: start,
        endDate: end,
        sourceType: source,
        paymentMethod: method
      };

      // Load payment history
      const historyResponse = await getPaymentHistory(params);
      const historyData = historyResponse?.data?.payload || historyResponse?.payload || historyResponse?.data || [];
      setPayments(Array.isArray(historyData) ? historyData : []);

      // Load statistics
      try {
        const statsResponse = await getPaymentStatistics({ startDate: start, endDate: end });
        const statsData = statsResponse?.data?.payload || statsResponse?.payload || statsResponse?.data;
        setStatistics(statsData);
      } catch (statsError) {
        console.warn('Statistics endpoint not available, calculating from data');
        setStatistics(null);
      }

    } catch (error) {
      console.error('Error loading payment data:', error);
      toast.error(t('failed_to_load_payment_data') || 'Failed to load payment data');
      setPayments([]);
      setStatistics(null);
    } finally {
      setLoading(false);
      setStatisticsLoading(false);
    }
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    
    if (filter !== 'custom') {
      const dateRange = getDateRangeFromFilter(filter);
      setStartDate(dateRange.startDate);
      setEndDate(dateRange.endDate);
    }
  };

  const handleSearch = () => {
    loadPaymentData();
  };

  const handleClearFilters = () => {
    const dateRange = getDateRangeFromFilter('last30days');
    setDateFilter('last30days');
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
    setSourceType('');
    setPaymentMethod('');
    loadPaymentData(dateRange.startDate, dateRange.endDate, '', '');
  };

  const handleRefresh = () => {
    loadPaymentData();
  };

  const handleViewDetails = (payment) => {
    // Navigate to source document
    if (payment.SourceType === 'Invoice' && payment.InvoiceId) {
      navigate(`/invoices/${payment.InvoiceId}`);
    } else if (payment.SourceType === 'Offer' && payment.SourceId) {
      navigate(`/sales/offers/${payment.SourceId}`);
    } else if (payment.SourceType === 'Ticket' && payment.SourceId) {
      navigate(`/tickets/${payment.SourceId}`);
    }
  };

  const getSourceTypeChip = (sourceType) => {
    const colors = {
      'Invoice': 'primary',
      'Offer': 'secondary',
      'Ticket': 'info'
    };
    return (
      <Chip
        label={sourceType}
        color={colors[sourceType] || 'default'}
        size="small"
        sx={{ fontWeight: 500, fontSize: '0.75rem', height: '24px' }}
      />
    );
  };

  const getPaymentStatusChip = (status) => {
    const color = PAYMENT_STATUS_COLORS[status] || 'default';
    return (
      <Chip
        label={status}
        color={color}
        size="small"
        sx={{ fontWeight: 600, fontSize: '0.75rem', height: '24px' }}
      />
    );
  };

  const getPaymentMethodChip = (method) => {
    const colors = {
      'Cash': 'success',
      'Bancontact': 'info',
      'Veriment': 'primary',
      'Not Specified': 'default'
    };
    return (
      <Chip
        label={method}
        color={colors[method] || 'default'}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 500, fontSize: '0.75rem', height: '24px' }}
      />
    );
  };

  // Calculate summary statistics from payments
  const calculateSummary = () => {
    if (statistics) {
      return statistics;
    }

    const totalAmount = payments.reduce((sum, p) => sum + (p.Amount || 0), 0);
    const totalPayments = payments.length;
    const completedPayments = payments.filter(p => p.PaymentStatus === 'Completed').length;
    const partialPayments = payments.filter(p => p.IsPartialPayment).length;

    return {
      totalAmount,
      totalPayments,
      completedPayments,
      partialPayments
    };
  };

  const summary = calculateSummary();

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {t('payment_history') || 'Payment History'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('payment_history_desc') || 'View and manage all payment transactions'}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
          >
            {t('refresh') || 'Refresh'}
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('total_received') || 'Total Received'}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {statisticsLoading ? <CircularProgress size={20} /> : formatCurrency(summary.totalAmount)}
                  </Typography>
                </Box>
                <AttachMoneyIcon sx={{ fontSize: 40, color: 'success.light', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('total_payments') || 'Total Payments'}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {statisticsLoading ? <CircularProgress size={20} /> : summary.totalPayments}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 40, color: 'primary.light', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('completed_payments') || 'Completed'}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'info.main' }}>
                    {statisticsLoading ? <CircularProgress size={20} /> : summary.completedPayments}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'info.light', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('partial_payments') || 'Partial'}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    {statisticsLoading ? <CircularProgress size={20} /> : summary.partialPayments}
                  </Typography>
                </Box>
                <AssignmentIcon sx={{ fontSize: 40, color: 'warning.light', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t('filters') || 'Filters'}
        </Typography>
        <Grid container spacing={2}>
          {/* Date Filter */}
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('date_range') || 'Date Range'}</InputLabel>
              <Select
                value={dateFilter}
                onChange={(e) => handleDateFilterChange(e.target.value)}
                label={t('date_range') || 'Date Range'}
              >
                {DATE_FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('start_date') || 'Start Date'}
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setDateFilter('custom');
              }}
              InputLabelProps={{ shrink: true }}
              disabled={dateFilter !== 'custom'}
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('end_date') || 'End Date'}
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setDateFilter('custom');
              }}
              InputLabelProps={{ shrink: true }}
              disabled={dateFilter !== 'custom'}
            />
          </Grid>

          {/* Source Type */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('source_type') || 'Source Type'}</InputLabel>
              <Select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                label={t('source_type') || 'Source Type'}
              >
                {SOURCE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Method */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('payment_method') || 'Payment Method'}</InputLabel>
              <Select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                label={t('payment_method') || 'Payment Method'}
              >
                {PAYMENT_METHOD_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Search and Clear Buttons */}
          <Grid item xs={12} sm={12} md={1.5}>
            <Box sx={{ display: 'flex', gap: 1, height: '100%' }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
                sx={{ 
                  borderRadius: 1, 
                  textTransform: 'none', 
                  fontWeight: 500,
                  minHeight: '40px'
                }}
              >
                {t('search') || 'Search'}
              </Button>
              <IconButton
                color="secondary"
                onClick={handleClearFilters}
                disabled={loading}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1
                }}
                title={t('clear_filters') || 'Clear Filters'}
              >
                <ClearIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Payment History Table */}
      <Paper sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('payment_transactions') || 'Payment Transactions'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {payments.length} {t('payments_found') || 'payments found'}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('reference') || 'Reference'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('date') || 'Date'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('customer') || 'Customer'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('document') || 'Document'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('source_type') || 'Source'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('method') || 'Method'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">{t('amount') || 'Amount'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('status') || 'Status'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">{t('actions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        {t('no_payments_found') || 'No payments found for the selected filters'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.Id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, fontSize: '0.85rem' }}>
                          {payment.PaymentReference}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {formatDateTime(payment.PaymentDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {payment.CustomerName || t('walk_in_customer') || 'Walk-in Customer'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {payment.SourceDocumentNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{getSourceTypeChip(payment.SourceType)}</TableCell>
                      <TableCell>{getPaymentMethodChip(payment.PaymentMethod)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {formatCurrency(payment.Amount)}
                        </Typography>
                        {payment.RemainingBalance > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Remaining: {formatCurrency(payment.RemainingBalance)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{getPaymentStatusChip(payment.PaymentStatus)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(payment)}
                          sx={{ color: 'primary.main' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default PaymentPage;